import { Settings2 } from "lucide-react";
import { useEffect, useState } from "react";
import FoodLogger from "../components/log/FoodLogger";
import MealHistory from "../components/log/MealHistory";
import VitalsModal from "../components/log/VitalsModal";
import WeightTracker from "../components/log/WeightTracker";
import WorkoutTracker from "../components/log/WorkoutTracker";
import LoadingMeal from "../components/shared/LoadingMeal";
import { useAuth } from "../utils/AuthContext";
import { useProfile } from "../hooks/useProfile";
import { useMeals } from "../hooks/useMeals";
import { GuestBanner } from "../components/shared/GuestBanner";

export default function LogScreen({ onShowAuth, setIsPullToRefreshEnabled }) {
  const { user, loading: authLoading, isGuest, uid } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [openVitalsModal, setOpenVitalsModal] = useState(false);

  // 1. Hook-based Data Fetching (shared across components)
  const { data: profileData, isLoading: profileLoading } = useProfile(uid);

  const { data: meals = [], isLoading: mealsLoading } = useMeals(uid);

  // 2. Automated Modal Logic — only for logged-in users
  useEffect(() => {
    if (
      !isGuest &&
      !profileLoading &&
      user &&
      (!profileData || !profileData.activityLevel)
    ) {
      const timer = setTimeout(() => setOpenVitalsModal(true), 0);
      return () => clearTimeout(timer);
    }
  }, [profileData, profileLoading, user, isGuest]);

  useEffect(() => {
    setIsPullToRefreshEnabled(!openVitalsModal);
  }, [openVitalsModal, setIsPullToRefreshEnabled]);

  // Loading guard (only wait for auth, not for guest)
  if (authLoading) return <LoadingMeal />;
  if (!isGuest && (profileLoading || mealsLoading)) return <LoadingMeal />;

  // 3. Derived Data for the current view
  const todayMeals = meals.filter((m) => {
    if (!m.createdAt) return false;
    const d = m.createdAt.toDate ? m.createdAt.toDate() : new Date(m.createdAt);
    return d.toDateString() === new Date().toDateString();
  });

  const tabs = [
    { label: "Meals", id: 0 },
    { label: "Weight", id: 1 },
    { label: "Workout", id: 2 },
  ];

  const displayName = user?.displayName || user?.email?.split("@")[0] || "Guest";

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Guest Banner */}
      {isGuest && <GuestBanner onShowAuth={onShowAuth} />}

      {/* Header */}
      <div className="flex items-start justify-between mb-8 group">
        <div>
          <p className="text-[9px] uppercase tracking-[0.2em] text-brand-dim font-sans mb-1.5 opacity-60">
            {isGuest ? "Guest Mode" : "Member Dashboard"}
          </p>
          <h1 className="font-serif text-3xl text-brand-text tracking-tight">
            {displayName}
          </h1>
        </div>

        <button
          onClick={() => setOpenVitalsModal(true)}
          className="p-3 rounded-2xl bg-white/[0.03] border border-white/5 text-gray-500 hover:text-brand-green transition-all"
        >
          <Settings2 size={20} />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            className={`relative z-10 px-6 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest select-none flex-shrink-0 ${
              activeTab === tab.id
                ? "bg-brand-green text-brand-bg shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                : "bg-white/[0.03] text-gray-500 border border-white/5 active:bg-white/[0.08]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="border-b border-white/5 mb-8" />

      {/* Main Content Areas */}
      <div className="space-y-8">
        {activeTab === 0 && (
          <>
            <FoodLogger
              todayMeals={todayMeals}
              onShowAuth={onShowAuth}
              onSetupGoals={() => setOpenVitalsModal(true)}
            />
            <div className="border-b border-white/5" />
            {/* MealHistory now fetches its own data/profile internally */}
            <MealHistory onShowAuth={onShowAuth} />
          </>
        )}

        {activeTab === 1 && (
          <WeightTracker setOpenVitalsModal={setOpenVitalsModal} onShowAuth={onShowAuth} />
        )}

        {activeTab === 2 && <WorkoutTracker uid={uid} onShowAuth={onShowAuth} />}
      </div>

      {openVitalsModal && (
        <VitalsModal uid={uid} onClose={() => setOpenVitalsModal(false)} />
      )}
    </div>
  );
}
